import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { IconClose } from '@belong/icons';
import { ButtonBase } from '../ButtonBase/ButtonBase.js';
import { Text } from '../Text/Text.js';

const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
const INTERCOM_LAUNCHER_Z_INDEX = 2147483000;
const ICON_URL = 'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/common-assets/ic_cone.svg';
function getStorageKey(id) {
  return `maintenance-notice-dismissed:${id}`;
}
function isDismissed(id) {
  try {
    const dismissedAt = Number(window.localStorage.getItem(getStorageKey(id)));
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}
function isWithinWindow(startsAt, endsAt) {
  const now = Date.now();
  return now >= Date.parse(startsAt) && now < Date.parse(endsAt);
}
function renderMessage(message, highlight) {
  const index = highlight ? message.indexOf(highlight) : -1;
  if (!highlight || index === -1) {
    return message;
  }
  return jsxs(Fragment, {
    children: [message.slice(0, index), jsx("mark", {
      className: "rounded-sm bg-dark-green/20 px-1 font-semibold text-navy",
      children: highlight
    }), message.slice(index + highlight.length)]
  });
}
function MaintenanceNoticeToast({
  id,
  title,
  message,
  highlight,
  startsAt,
  endsAt
}) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (isDismissed(id)) {
      return;
    }
    setIsVisible(isWithinWindow(startsAt, endsAt));
  }, [id, startsAt, endsAt]);
  function handleClose() {
    setIsVisible(false);
    try {
      window.localStorage.setItem(getStorageKey(id), String(Date.now()));
    } catch {
      // Dismissal just won't persist.
    }
  }
  if (!isVisible) {
    return null;
  }
  return jsxs("div", {
    role: "status",
    "aria-live": "polite",
    className: "fixed bottom-sm left-sm right-sm z-toast md:bottom-xl md:left-auto md:right-xl md:w-[400px]",
    style: {
      zIndex: INTERCOM_LAUNCHER_Z_INDEX + 1,
      animation: 'maintenanceNoticeSlideIn 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
    },
    children: [jsx("style", {
      children: `@keyframes maintenanceNoticeSlideIn{from{opacity:0;transform:translate3d(120%,0,0)}to{opacity:1;transform:translate3d(0,0,0)}}`
    }), jsxs("div", {
      className: "relative rounded bg-white p-sm shadow-[0_0_24px_rgba(0,0,0,0.2)]",
      children: [jsxs("div", {
        className: "flex flex-col gap-xs",
        children: [jsxs("div", {
          className: "flex items-center gap-xs",
          children: [jsx("img", {
            alt: "",
            height: 20,
            src: ICON_URL,
            width: 20
          }), jsx(Text, {
            as: "span",
            className: "text-dark-green",
            fontWeight: "semibold",
            children: title
          })]
        }), jsx(Text, {
          as: "span",
          className: "text-navy",
          children: renderMessage(message, highlight)
        })]
      }), jsx(ButtonBase, {
        "aria-label": "Dismiss notice",
        className: "absolute flex items-center justify-center bg-navy p-0 transition-colors hover:bg-dark-navy",
        style: {
          top: -10,
          right: -10,
          width: 24,
          height: 24,
          borderRadius: 9999,
          boxShadow: '0 0 8px rgba(0,0,0,0.25)'
        },
        onClick: handleClose,
        children: jsx(IconClose, {
          className: "text-white",
          height: 10,
          width: 10
        })
      })]
    })]
  });
}

export { MaintenanceNoticeToast };
